/* Super Vitality — motion layer.
 *
 * Two jobs: reveal elements as they scroll in, and make the marquee loop
 * seamlessly. Both have to cope with Horizon moving the scroll container
 * between breakpoints, and with sections being re-rendered in the editor.
 */
(function () {
  'use strict';

  var REVEAL = '[data-sv-reveal]';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Horizon makes .page-wrapper the scroll container at >=990px and lets the
   * document scroll below that. Rather than hardcode the breakpoint, ask the
   * element whether it is actually scrolling — that survives theme changes. */
  function scrollRoot() {
    var wrapper = document.querySelector('.page-wrapper');
    if (!wrapper) return null;
    var overflowY = getComputedStyle(wrapper).overflowY;
    var scrolls = overflowY === 'auto' || overflowY === 'scroll';
    return scrolls && wrapper.scrollHeight > wrapper.clientHeight ? wrapper : null;
  }

  var observer = null;
  var currentRoot;

  function reveal(el) {
    el.classList.add('sv-in');
  }

  function buildObserver() {
    var root = scrollRoot();
    // Nothing to do if the root has not actually changed.
    if (observer && root === currentRoot) return;

    if (observer) observer.disconnect();
    currentRoot = root;

    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll(REVEAL).forEach(reveal);
      return;
    }

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target); // reveal once, then stop watching
        });
      },
      {root: root, rootMargin: '0px 0px -12% 0px', threshold: 0.12}
    );

    observe(document);
    revealInView();
  }

  /* The -12% bottom margin is right for sections further down the page: it
   * holds a reveal back until the element is properly in view rather than
   * clipping the bottom edge. It is wrong for anything on the first screen.
   * On a 660px phone viewport the hero product starts at y=604, which is below
   * the shrunken window, so it sat at opacity 0 until you scrolled past it —
   * the hero read as an empty colour block. Anything already on screen at load
   * reveals now instead of waiting for a scroll that may never happen. */
  function revealInView() {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    document.querySelectorAll(REVEAL).forEach(function (el) {
      if (el.classList.contains('sv-in')) return;
      var box = el.getBoundingClientRect();
      if (box.top < vh && box.bottom > 0) {
        reveal(el);
        if (observer) observer.unobserve(el);
      }
    });
  }

  function observe(scope) {
    var items = scope.querySelectorAll(REVEAL);
    if (reduced.matches || !observer) {
      items.forEach(reveal);
      return;
    }
    items.forEach(function (el) {
      if (el.classList.contains('sv-in')) return;
      // Stagger siblings so a grid arrives in sequence rather than all at once.
      var group = el.closest('[data-sv-stagger]');
      if (group && !el.style.getPropertyValue('--sv-delay')) {
        var peers = Array.prototype.slice.call(group.querySelectorAll(REVEAL));
        var step = parseInt(group.getAttribute('data-sv-stagger'), 10) || 70;
        el.style.setProperty('--sv-delay', Math.min(peers.indexOf(el), 8) * step + 'ms');
      }
      observer.observe(el);
    });
  }

  /* A marquee only loops seamlessly if the track is exactly two copies of the
   * content, because the keyframe translates by -50%. Duplicate it here rather
   * than asking whoever writes the section to paste the markup twice. */
  function buildMarquee(scope) {
    scope.querySelectorAll('.sv-marquee__track').forEach(function (track) {
      if (track.dataset.svCloned === 'true') return;
      // Clone the live nodes rather than round-tripping through innerHTML.
      var copies = Array.prototype.map.call(track.children, function (child) {
        var clone = child.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true'); // duplicate is decorative
        return clone;
      });
      copies.forEach(function (clone) { track.appendChild(clone); });
      track.dataset.svCloned = 'true';
    });
  }

  /* Scroll scrubbing.
   * Publishes --sv-progress (0..1) on every [data-sv-scrub] element based on
   * how far it has travelled through the viewport. Because it is a position
   * readout rather than a played animation, scrolling back up runs it in
   * reverse for free. */
  var scrubEls = [];
  var scrubTicking = false;

  function updateScrub() {
    scrubTicking = false;
    var root = currentRoot;
    var vh = root ? root.clientHeight : window.innerHeight;
    var top = root ? root.scrollTop : (window.pageYOffset || document.documentElement.scrollTop);

    for (var i = 0; i < scrubEls.length; i++) {
      var el = scrubEls[i];
      var p;

      if (el.getAttribute('data-sv-scrub') === 'page') {
        /* Hero mode. An element that is already on screen at load would sit at
         * ~0.5 under the travel formula, so the drop would start detached from
         * the pipette. Measuring distance scrolled from the top instead means
         * progress is a true 0 when the page is at rest. */
        p = top / (vh * 0.9);
      } else {
        var r = el.getBoundingClientRect();
        // 0 as the element's top reaches the bottom of the viewport,
        // 1 once its bottom has passed the top.
        p = (vh - r.top) / (vh + r.height);
      }

      p = p < 0 ? 0 : p > 1 ? 1 : p;
      el.style.setProperty('--sv-progress', p.toFixed(4));
    }
    updateScrubVideo();
  }

  function requestScrub() {
    if (scrubTicking) return;
    scrubTicking = true;
    requestAnimationFrame(updateScrub);
  }

  function initScrub(scope) {
    var found = (scope || document).querySelectorAll('[data-sv-scrub]');
    for (var i = 0; i < found.length; i++) {
      if (scrubEls.indexOf(found[i]) === -1) scrubEls.push(found[i]);
    }
    if (!scrubEls.length) return;

    if (reduced.matches) {
      scrubEls.forEach(function (el) { el.style.setProperty('--sv-progress', '0'); });
      return;
    }

    // Listen on whichever element actually scrolls, plus the window as backup.
    var target = currentRoot || window;
    target.removeEventListener('scroll', requestScrub);
    target.addEventListener('scroll', requestScrub, {passive: true});
    window.addEventListener('resize', requestScrub, {passive: true});
    updateScrub();
  }

  /* Scroll-scrubbed video.
   * Instead of playing, the video's currentTime is driven directly by scroll
   * position, so scrolling down runs it forward and scrolling up runs it
   * backwards. This is the technique behind the "product assembles as you
   * scroll" sequences. */
  var scrubVideos = [];

  function updateScrubVideo() {
    for (var i = 0; i < scrubVideos.length; i++) {
      var v = scrubVideos[i];
      if (!v.duration || isNaN(v.duration)) continue;
      var host = v.closest('[data-sv-scrub]') || v;
      var p = parseFloat(host.style.getPropertyValue('--sv-progress')) || 0;
      var t = p * (v.duration - 0.05);
      // Skip sub-frame seeks; they cause visible stutter without changing much.
      if (Math.abs(v.currentTime - t) > 0.02) {
        try { v.currentTime = t; } catch (e) { /* not seekable yet */ }
      }
    }
  }

  function initScrubVideo(scope) {
    var found = (scope || document).querySelectorAll('video[data-sv-scrub-video]');
    /* The markup ships preload="none" so a phone never pulls the sequence down.
     * Scrubbing is a pointer-and-scroll affordance that iOS cannot honour
     * anyway — Safari will not decode frames on seek without a play() gesture —
     * so below 900px the poster still is the whole hero and the mp4 stays
     * unfetched. */
    if (window.innerWidth < 900) return;
    for (var i = 0; i < found.length; i++) {
      var v = found[i];
      if (scrubVideos.indexOf(v) !== -1) continue;
      v.pause();
      v.removeAttribute('autoplay');
      if (v.preload === 'none') {
        v.preload = 'auto';
        v.load(); // preload="none" means nothing is fetched until asked
      }
      // Seeking needs metadata; without it duration is NaN and nothing moves.
      if (v.readyState >= 1) {
        scrubVideos.push(v);
      } else {
        (function (vid) {
          vid.addEventListener('loadedmetadata', function () {
            if (scrubVideos.indexOf(vid) === -1) scrubVideos.push(vid);
            updateScrubVideo();
          }, {once: true});
        })(v);
      }
    }
  }

  function init(scope) {
    buildMarquee(scope || document);
    buildObserver();
    initScrub(scope);
    initScrubVideo(scope);
    if (scope && scope !== document) observe(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Fonts and the hero media settle after DOMContentLoaded and can push an
   * element into or out of the first screen, so check again once it has. */
  window.addEventListener('load', revealInView);

  // The scroll container swaps at the 990px breakpoint — rebuild when it does.
  // buildObserver early-returns when the root is unchanged, so the in-view
  // sweep is called separately rather than relying on it.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      buildObserver();
      revealInView();
    }, 180);
  });

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  reduced.addEventListener('change', function () {
    if (reduced.matches) document.querySelectorAll(REVEAL).forEach(reveal);
  });
})();
